import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { getBackendUrl } from '../../utils/utils';
import './Login.scss';

// Public Google Cloud Marketplace listing for Reality Coach (same target used on
// the marketplace signup screens) so a visitor with no subscription can purchase one.
const MARKETPLACE_URL =
  'https://console.cloud.google.com/marketplace/product/da-realitycoach-prod/reality-coach';

const NoOrganizationPage: React.FC = () => {
  const { logout, currentUser, error, isMarketplaceUser, token } = useAuth();

  // A Terms-of-Service / policy 451 is meaningless on this page: a user with no
  // org can't be shown the policy modal anyway, so surfacing it just confuses them.
  // Suppress it so they always get the friendly "not assigned to an org" message.
  const isPolicyError = !!error && /terms of service|privacy policy|policy/i.test(error);
  const displayError = isPolicyError ? null : error;

  // No specific error AND no marketplace_account_id => this account has no
  // subscription at all. This is the "signed in with Google on the login page
  // without ever buying" case, which is different from a real Marketplace user
  // whose org assignment is merely pending. Give them a clear "no subscription,
  // here's how to get one" message instead of "contact your administrator".
  const noSubscription = !displayError && !isMarketplaceUser;

  // A "no subscription" account can actually be an orphaned subscriber: an admin
  // deleted their account (removing the Keycloak identity + org link) while their
  // Marketplace subscription is still ACTIVE/PENDING. The backend detects this by
  // matching the signed-in email against MarketplaceEntitlement.purchaser_email.
  // When true we swap the generic "No Subscription Found" copy for a "your account
  // was removed but your subscription is still active — contact support" message.
  const [subscriptionActiveButRemoved, setSubscriptionActiveButRemoved] = useState(false);

  useEffect(() => {
    if (!noSubscription || !token) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await axios.get(`${getBackendUrl()}/marketplace/orphaned-subscription`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled && (resp.data as { has_active_subscription?: boolean })?.has_active_subscription) {
          setSubscriptionActiveButRemoved(true);
        }
      } catch {
        // Best-effort enrichment — on any failure we simply fall back to the
        // generic "No Subscription Found" message.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [noSubscription, token]);

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-card-header">
          <img src="/assets/branding/rc-logo-dark.png" alt="Reality Coach" className="login-logo" />
          <h1 className="login-title">
            {subscriptionActiveButRemoved
              ? 'Account Access Removed'
              : noSubscription
                ? 'No Subscription Found'
                : 'No Organization Assigned'}
          </h1>
          {currentUser?.email && (
            <p className="login-subtitle">Signed in as <strong>{currentUser.email}</strong></p>
          )}
        </div>

        {displayError ? (
          /* Specific claim error (e.g. 409 duplicate identity). */
          <div style={{
            background: '#fef2f2',
            border: '1px solid #fca5a5',
            borderRadius: '8px',
            padding: '0.75rem 1rem',
            marginBottom: '1.25rem',
          }}>
            <p style={{ color: '#991b1b', fontSize: '0.875rem', lineHeight: '1.5', margin: 0 }}>
              {displayError}
            </p>
          </div>
        ) : subscriptionActiveButRemoved ? (
          /* Orphaned subscriber: account was deleted by an admin, but the
             Marketplace subscription is still active. Point them at support to
             restore access — they must NOT be told to re-purchase. */
          <>
            <p style={{ color: '#475569', fontSize: '0.9rem', lineHeight: '1.6', marginBottom: '1.25rem', textAlign: 'center' }}>
              Your account was removed by an administrator, but your Marketplace subscription
              is still active. Please contact support to restore your access.
            </p>
            <div style={{
              background: '#fffbeb',
              border: '1px solid #f59e0b',
              borderRadius: '8px',
              padding: '0.75rem 1rem',
              marginBottom: '1.25rem',
            }}>
              <p style={{ color: '#92400e', fontSize: '0.8125rem', lineHeight: '1.5', margin: '0 0 6px' }}>
                We couldn’t find an active account for this email, but your Google Cloud
                Marketplace subscription is still in good standing.
              </p>
              <p style={{ margin: 0, fontSize: '0.8125rem', color: '#92400e' }}>
                Please contact{' '}
                <a href="mailto:tech@realitycoach.ai" style={{ color: '#005581', fontWeight: '600', textDecoration: 'none' }}>
                  tech@realitycoach.ai
                </a>{' '}
                to have your access restored. Do not purchase a new subscription.
              </p>
            </div>
            <a
              className="login-btn login-btn--primary"
              href="mailto:tech@realitycoach.ai?subject=Restore%20Reality%20Coach%20access"
              style={{ textDecoration: 'none', marginBottom: '0.75rem' }}
            >
              Contact Support
            </a>
          </>
        ) : noSubscription ? (
          /* No subscription on this account: point them at the Marketplace. */
          <>
            <p style={{ color: '#475569', fontSize: '0.9rem', lineHeight: '1.6', marginBottom: '1.25rem', textAlign: 'center' }}>
              We couldn’t find a Reality Coach subscription for this account.
            </p>
            <div style={{
              background: '#fffbeb',
              border: '1px solid #f59e0b',
              borderRadius: '8px',
              padding: '0.75rem 1rem',
              marginBottom: '1.25rem',
            }}>
              <p style={{ color: '#92400e', fontSize: '0.8125rem', lineHeight: '1.5', margin: '0 0 6px' }}>
                Reality Coach is purchased through Google Cloud Marketplace. Subscribe there to get
                started, then come back and sign in with this Google account.
              </p>
              <p style={{ margin: '0 0 6px', fontSize: '0.8125rem', color: '#92400e' }}>
                <strong>Already have an account?</strong> Sign out and sign in with the email and
                password you used.
              </p>
              <p style={{ margin: 0, fontSize: '0.8125rem', color: '#92400e' }}>
                Need help?{' '}
                <a href="mailto:tech@realitycoach.ai" style={{ color: '#005581', fontWeight: '600', textDecoration: 'none' }}>
                  tech@realitycoach.ai
                </a>
              </p>
            </div>
            <a
              className="login-btn login-btn--primary"
              href={MARKETPLACE_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'none', marginBottom: '0.75rem' }}
            >
              Go to Google Cloud Marketplace
            </a>
          </>
        ) : (
          /* Marketplace user whose org assignment is pending / not yet linked. */
          <>
            <p style={{ color: '#475569', fontSize: '0.9rem', lineHeight: '1.6', marginBottom: '1.25rem', textAlign: 'center' }}>
              Your account has not been assigned to an organization.
              Please contact your administrator to gain access.
            </p>
            <div style={{
              background: '#fffbeb',
              border: '1px solid #f59e0b',
              borderRadius: '8px',
              padding: '0.75rem 1rem',
              marginBottom: '1.25rem',
            }}>
              <p style={{ color: '#92400e', fontSize: '0.8125rem', lineHeight: '1.5', margin: '0 0 6px' }}>
                Your account exists but has not been linked to any organization.
                Reach out to your platform administrator to have your organization assignment completed.
              </p>
              <p style={{ margin: '0 0 6px', fontSize: '0.8125rem', color: '#92400e' }}>
                <strong>Signed in with Google?</strong> If you previously created an account using
                an email and password, sign out and use your email and password to sign in instead.
              </p>
              <p style={{ margin: 0, fontSize: '0.8125rem', color: '#92400e' }}>
                Need help?{' '}
                <a href="mailto:tech@realitycoach.ai" style={{ color: '#005581', fontWeight: '600', textDecoration: 'none' }}>
                  tech@realitycoach.ai
                </a>
              </p>
            </div>
          </>
        )}

        {/* Marketplace users with a duplicate-identity error can retry the setup flow */}
        {displayError && isMarketplaceUser && (
          <div style={{ marginBottom: '1rem', textAlign: 'center', fontSize: '0.875rem', color: '#6b7280' }}>
            Purchased via Google Cloud Marketplace?{' '}
            <a href="/marketplace-signup" style={{ color: '#005581', fontWeight: 600, textDecoration: 'none' }}>
              Set up your account →
            </a>
          </div>
        )}

        <button
          className={`login-btn ${noSubscription ? 'login-btn--google' : 'login-btn--primary'}`}
          onClick={() => logout()}
        >
          Log Out
        </button>
      </div>
    </div>
  );
};

export default NoOrganizationPage;
